import { IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient } from '@frontend/package/api';
import { PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { StockExtended } from '@frontend/stock/types';
import { TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { useEffect, useState } from 'react';

import { useLicenseRepository } from '../../../utils/license-repository';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    product?: IProduct;
    equipment?: IEquipment;
    stock?: StockExtended;
    slot: Slot | null;
    isProcessing: boolean;
    handleSubmit: () => void;
}

const useLendingReviewDetail = (): ViewProps => {
    const { store, wizardId, navigationState, onSubmit } = useScreen();
    const { getFunctionality, getFunctionalitySetting } = useLicenseRepository();
    const auditFunctionality = getFunctionality(Application.LENDING, Feature.AUDIT_TRANSACTION);
    const [isProcessing, setIsProcessing] = useState(false);
    const [slot, setSlot] = useState<Slot | null>(null);

    const product = store?.data?.product;
    const equipment = store?.data?.equipment;
    const stock = store?.data?.stock;

    const { syncTransaction } = useWizardTransaction({
        wizardId,
        store
    });

    const { localUpdatePackage } = useWizardPackage({
        store: store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });

    useEffect(() => {
        if (!stock) return;
        SlotClient.fetchSlot(stock.account_id, stock.spot_id, stock.module_id, stock.slot_id).then(setSlot);
    }, [stock]);

    const handleSubmit = async () => {
        if (!auditFunctionality || !stock || !product || !equipment) return;
        const settings = getFunctionalitySetting(auditFunctionality.id);
        const trxWF = settings[SettingKey.TRANSACTION_WORKFLOW_ID] as string | undefined;
        const pkgWF = settings[SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID] as string | undefined;
        if (!trxWF || !pkgWF) return;
        setIsProcessing(true);
        try {
            const trx = await syncTransaction({
                application: Application.LENDING,
                feature: Feature.AUDIT_TRANSACTION,
                state: TransactionStateName.TRANSACTION_CREATED,
                workflow_id: trxWF,
                type: TransactionType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                user_id: navigationState.user?.id || undefined,
                iot_id: navigationState.iot?.id || undefined
            });

            const pkg = await PackageClient.postPackage(trx.account_id, trx.id, {
                application: Application.LENDING,
                feature: Feature.AUDIT_TRANSACTION,
                workflow_id: pkgWF,
                user_id: trx.user_id,
                quantity: 1,
                type: PackageType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                product_id: product.id,
                equipment_id: equipment.id,
                priority: 1,
                blocking: false
            });
            await localUpdatePackage(pkg);
            await TransactionWorkflowClient.triggerTransactionState(trx.account_id, trx.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
            onSubmit();
        } finally {
            setIsProcessing(false);
        }
    };

    return {
        product,
        equipment,
        stock,
        slot,
        isProcessing,
        handleSubmit
    };
};

export default useLendingReviewDetail;
